// @ts-strict-ignore
import { computed, inject, Injectable } from '@angular/core';
import { AuthService, Customer, CustomerApiService, Employee } from '@fc-core';
import { EmployeeApiService } from '../../../employees/services/employee-api.service';
import { lastValueFrom } from 'rxjs';

import { Store } from '@fc-core/store-utils/store';

interface CustomerSelectState {
  customerList: Customer[];
  employeeList: Employee[];
  customerSearch: string;
  employeeSearch: string;
  employeeLoading: boolean;
  customerLoading: boolean;
  isEmployeeSelectorOpen: boolean;
  customerIndex: number;
  employeeIndex: number;
}

const initialCustomerSelectState: CustomerSelectState = {
  customerList: [],
  employeeList: [],
  customerSearch: '',
  employeeSearch: '',
  employeeLoading: false,
  customerLoading: false,
  isEmployeeSelectorOpen: false,
  customerIndex: 0,
  employeeIndex: 0,
};

@Injectable()
export class CustomerSelectStore extends Store<CustomerSelectState> {
  constructor() {
    super(initialCustomerSelectState);
  }
  customerApi = inject(CustomerApiService);
  employeeApiService = inject(EmployeeApiService);
  auth = inject(AuthService);

  // selectors
  customerSearch = computed(() => this.state().customerSearch);
  employeeSearch = computed(() => this.state().employeeSearch);
  isOpen = computed(() => this.state().isEmployeeSelectorOpen);
  employeeList = computed(() =>
    this.state().employeeList.filter((employee) =>
      `${employee.firstName} ${employee.lastName}`
        .toLowerCase()
        .includes(this.employeeSearch().toLowerCase()),
    ),
  );
  customerIndex = computed(() => this.state().customerIndex);
  employeeIndex = computed(() => this.state().employeeIndex);
  customerList = computed(() =>
    this.state().customerList.filter((customer) =>
      customer.name.toLowerCase().includes(this.customerSearch().toLowerCase()),
    ),
  );
  employeeLoading = computed(() => this.state().employeeLoading);
  customerLoading = computed(() => this.state().customerLoading);

  // actions
  setCustomerIndex(index: number) {
    this.draftUpdate((draft) => {
      draft.customerIndex = index;
    });
  }

  setEmployeeIndex(index: number) {
    this.draftUpdate((draft) => {
      draft.employeeIndex = index;
    });
  }

  setEmployeeSelectorOpen(isOpen: boolean) {
    this.draftUpdate((draft) => {
      draft.isEmployeeSelectorOpen = isOpen;
    });
  }

  setCustomerSearch(search: string) {
    this.draftUpdate((draft) => {
      draft.customerSearch = search;
      draft.customerIndex = 0;
    });
  }

  setEmployeeSearch(search: string) {
    this.draftUpdate((draft) => {
      draft.employeeSearch = search;
      draft.employeeIndex = 0;
    });
  }

  async getEmployeeList(customerId: string) {
    this.draftUpdate((draft) => {
      draft.employeeLoading = true;
      draft.employeeList = [];
      draft.isEmployeeSelectorOpen = true;
    });
    const resp = await lastValueFrom(
      this.employeeApiService.loadEmployees(customerId),
    );
    this.draftUpdate((draft) => {
      draft.employeeList = resp.list;
      draft.employeeLoading = false;
    });
  }

  async getCustomerList() {
    this.draftUpdate((draft) => {
      draft.customerLoading = true;
      draft.customerList = [];
    });
    const resp = await lastValueFrom(this.customerApi.loadAll());
    this.draftUpdate((draft) => {
      draft.customerList = resp;
      draft.customerLoading = false;
    });
  }

  async switchCustomer(customer: Customer) {
    await lastValueFrom(this.auth.switchToCustomerDev(customer.id));
  }

  async switchEmployee(employee: Employee) {
    const customerId = this.customerList()[this.customerIndex()].id;
    await lastValueFrom(this.auth.switchToEmployee(employee.id, customerId));
  }
}
